import { ComboboxItem, Group, Stack, Text } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderList, RenderListProps } from './render-list';

export interface TransferListProps {
  onChange: (selected: string[]) => void;
  initialSelected?: ComboboxItem[];
  availableOptions?: ComboboxItem[];
  loading?: boolean;
  searchable?: boolean;
  onSearch?: (search: string) => void;
  onPageChange?: (page: number) => void;
  pages?: number;
  leftTitle?: string;
  rightTitle?: string;
  leftSearchPlaceholder?: string;
  rightSearchPlaceholder?: string;
}

export function TransferList({
  onChange,
  initialSelected = [],
  availableOptions = [],
  loading = false,
  searchable = true,
  onSearch,
  onPageChange,
  pages,
  leftTitle,
  rightTitle,
  leftSearchPlaceholder,
  rightSearchPlaceholder,
}: TransferListProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<[ComboboxItem[], ComboboxItem[]]>([
    availableOptions,
    initialSelected,
  ]);

  const handleSearch: RenderListProps['onSearch'] = useDebouncedCallback(
    (type, search) => {
      if (type === 'forward' && onSearch) {
        onSearch(search);
      }
    },
    300,
  );

  const handleTransfer = (transferFrom: number, options: string[]) =>
    setData((current) => {
      const transferTo = transferFrom === 0 ? 1 : 0;
      const transferFromData = current[transferFrom].filter(
        (item) => !options.some((option) => option === item.value),
      );
      const selectedOptions = current[transferFrom].filter((item) =>
        options.some((option) => option === item.value),
      );
      const transferToData = [...current[transferTo], ...selectedOptions];

      const result = [];
      result[transferFrom] = transferFromData;
      result[transferTo] = transferToData;

      // Call onChange with the selected items' values
      const selected = result[1].map((item) => item.value);
      onChange(selected);

      return result as [ComboboxItem[], ComboboxItem[]];
    });

  useEffect(() => {
    setData(([, selected]) => [availableOptions, selected]);
  }, [availableOptions]);

  useEffect(() => {
    setData(([available]) => [available, initialSelected]);
  }, [initialSelected]);

  return (
    <Group align="start" grow>
      <Stack gap="xs">
        <Text ta="center">{leftTitle || t('common.available')}</Text>
        <RenderList
          loading={loading}
          onPageChange={onPageChange}
          onSearch={onSearch && handleSearch}
          onTransfer={(opts: string[]) => handleTransfer(0, opts)}
          options={data[0].filter(
            (item) =>
              !data[1].some((selected) => selected.value === item.value),
          )}
          pages={pages}
          searchPlaceholder={leftSearchPlaceholder}
          searchable={searchable}
          type="forward"
        />
      </Stack>
      <Stack gap="xs">
        <Text ta="center">{rightTitle || t('common.selected')}</Text>
        <RenderList
          onTransfer={(opts: string[]) => handleTransfer(1, opts)}
          options={data[1]}
          searchPlaceholder={rightSearchPlaceholder}
          searchable={searchable}
          type="backward"
        />
      </Stack>
    </Group>
  );
}

export default TransferList;
